import type { Navigate, Route } from "../router.ts";
import type { ShellState } from "../state.ts";
import { foundationsRoute } from "./foundations.ts";
import {
  DEMO_INDEX_PATH,
  DEMO_SCENARIOS,
  demoPath,
  receiverDemoIndexRoute,
  receiverDemoRoute,
} from "./receiver-demo.ts";
import { receiverReferenceRoute } from "./receiver-reference.ts";
import { composeRoute, reviewRoute, statusRoute } from "./transfer.ts";

/**
 * The application's route table.
 *
 * Extracted from `main.ts` so it can be asserted rather than only executed.
 * `frontend/test/receiver-boundary.test.ts` reads this table and holds two
 * properties that are easy to lose in a later refactor:
 *
 * 1. **No route carries an identifier.** No path has a parameter segment, no
 *    path is claim-shaped, and nothing in a path is ever read as data. The claim
 *    secret lives in the URL fragment of the normative claim URL and PV-16 is
 *    still validating that behaviour; a claim-shaped route here would be the
 *    first half of a claim entry point.
 * 2. **The claimant surface is reachable only from the demo namespace.** Five
 *    fixed paths under `/receiver-demo/`, one per synthetic journey, each
 *    registered as a literal below — so the surface can be reached the way a
 *    real one eventually will be, by tapping a link, without this build taking
 *    any position on where the production experience is hosted. Every other
 *    route renders none of it.
 */
export function appRoutes(state: ShellState): readonly Route[] {
  return [
    {
      path: "/",
      title: "Enter an amount",
      width: "wide",
      render: (navigate) => composeRoute(state, navigate),
    },
    {
      path: "/review",
      title: "Review transfer",
      render: (navigate) => reviewRoute(state, navigate),
    },
    {
      path: "/status",
      title: "Transfer status",
      render: (navigate) => statusRoute(state, navigate),
    },
    {
      path: "/receiver-reference",
      title: "Receiver claim experience",
      render: () => receiverReferenceRoute(state),
    },
    {
      path: DEMO_INDEX_PATH,
      title: "Direct-entry demo links",
      render: () => receiverDemoIndexRoute(state),
    },
    // One literal route per journey. Expanded here rather than matched by a
    // pattern: a `/receiver-demo/:scenario` route would be a parser, and a
    // parser reading a path segment is the shape this milestone must not have.
    ...DEMO_SCENARIOS.map((demo) => ({
      path: demoPath(demo),
      title: "Claim",
      chrome: "bare" as const,
      render: (navigate: Navigate) => receiverDemoRoute(state, demo, navigate),
    })),
    {
      path: "/foundations",
      title: "Design foundations",
      width: "wide",
      render: () => foundationsRoute(state),
    },
  ];
}
